import type { FieldDef, StatusDef } from "./model";

export interface KindTemplate {
  key: string;
  name: string;
  icon: string;
  fields: FieldDef[];
  statuses: StatusDef[];
}

export const WORK_STATUSES: StatusDef[] = [
  { key: "backlog", label: "Backlog", category: "todo" },
  { key: "ready", label: "Ready", category: "todo" },
  { key: "in_progress", label: "In progress", category: "active", wip: 6 },
  { key: "review", label: "Review", category: "review" },
  { key: "done", label: "Done", category: "done" },
];

export const DECISION_STATUSES: StatusDef[] = [
  { key: "proposed", label: "Proposed", category: "todo" },
  { key: "accepted", label: "Accepted", category: "active" },
  { key: "implemented", label: "Implemented", category: "review" },
  { key: "verified", label: "Verified", category: "done" },
];

export const PHASE_STATUSES: StatusDef[] = [
  { key: "planned", label: "Planned", category: "todo" },
  { key: "active", label: "Active", category: "active" },
  { key: "gate", label: "At gate", category: "review" },
  { key: "complete", label: "Complete", category: "done" },
];

export const DISCIPLINES = ["design", "code", "model", "rig", "animation", "vfx", "sfx", "ui", "art", "qa", "production"];

export const DISCIPLINE_LABELS: Record<string, string> = {
  design: "Design",
  code: "Programming",
  model: "Modeling",
  rig: "Rigging",
  animation: "Animation",
  vfx: "VFX",
  sfx: "SFX",
  ui: "UI",
  art: "Art",
  qa: "QA",
  production: "Production",
};

/** Kinds every new space starts with. Spaces can edit them or add their own. */
export const DEFAULT_KINDS: KindTemplate[] = [
  {
    key: "task",
    name: "Task",
    icon: "check",
    statuses: WORK_STATUSES,
    fields: [
      { key: "priority", label: "Priority", type: "select", options: ["urgent", "high", "medium", "low"] },
      { key: "discipline", label: "Discipline", type: "select", options: DISCIPLINES },
      { key: "estimate", label: "Estimate", type: "number", unit: "h" },
      { key: "acceptance", label: "Done when", type: "text" },
    ],
  },
  {
    key: "concept",
    name: "Concept",
    icon: "bulb",
    statuses: DECISION_STATUSES,
    fields: [
      { key: "pillar", label: "Pillar", type: "text" },
      { key: "scope", label: "Scope", type: "select", options: ["launch", "update", "later", "cut"] },
    ],
  },
  {
    key: "system",
    name: "System",
    icon: "cog",
    statuses: DECISION_STATUSES,
    fields: [{ key: "owner_discipline", label: "Discipline", type: "select", options: DISCIPLINES }],
  },
  {
    key: "phase",
    name: "Milestone",
    icon: "flag",
    statuses: PHASE_STATUSES,
    fields: [
      {
        key: "stage",
        label: "Stage",
        type: "select",
        options: ["concept", "pre-production", "vertical slice", "alpha", "beta", "launch", "live update", "event"],
      },
      { key: "gate", label: "Gate to leave", type: "text" },
    ],
  },
  {
    key: "doc",
    name: "Doc",
    icon: "doc",
    statuses: [
      { key: "draft", label: "Draft", category: "todo" },
      { key: "published", label: "Published", category: "done" },
    ],
    fields: [],
  },
];

export const SPACE_COLORS = ["#0071bc", "#4a6fa5", "#a33a3a", "#3f7a3a", "#5b3f7a", "#2f6f7a", "#8a4a2f", "#6d5f2f"];

/** What the "phase" item type is called: live games ship updates, games in development hit milestones. */
export const MILESTONE_WORD = { live: "Update", development: "Milestone" } as const;

/** Templates a new project starts with, by stage. Producers edit or delete them in project settings. */
export const STARTER_TEMPLATES: Record<"live" | "development", { name: string; kindKey: string; fields?: Record<string, string>; body?: string; checklist: string[] }[]> = {
  live: [
    {
      name: "Weekly update",
      kindKey: "phase",
      fields: { stage: "live update" },
      body: "What ships this week, and what players should notice.",
      checklist: ["Content locked", "QA pass on phone and PC", "Patch notes written", "Thumbnail and store art", "Published", "Announced in Discord"],
    },
    {
      name: "Monthly update",
      kindKey: "phase",
      fields: { stage: "live update" },
      body: "The month's headline feature, the event around it, and the numbers we want to move.",
      checklist: ["Feature locked", "Event configured and flags staged", "QA pass on phone and PC", "Patch notes written", "Trailer or teaser", "Thumbnail and store art", "Rollback plan", "Published", "Announced in Discord"],
    },
    {
      name: "Event",
      kindKey: "phase",
      fields: { stage: "event" },
      checklist: ["Rewards and drop rates set", "Event flags staged", "QA pass", "Start and end times checked", "Announced in Discord"],
    },
  ],
  development: [
    {
      name: "Sprint",
      kindKey: "phase",
      body: "The goal of this sprint in one sentence.",
      checklist: ["Sprint goal agreed", "Work assigned", "Playable build", "Team playtest", "Demo and retro"],
    },
    {
      name: "Milestone gate",
      kindKey: "phase",
      body: "What this milestone proves, and what has to be true to leave it.",
      checklist: ["Scope locked", "Playable build on phone and PC", "Playtest with outside players", "Performance budget met", "Go or no-go with the producer"],
    },
  ],
};
